import { AuthPageShell } from "@/auth/AuthPageShell";
import { signupWithInviteCode } from "@/auth/api/signup-api";
import { Button, InlineError, Input } from "@/components/ui";
import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";

export function SignupPage() {
  const [searchParams] = useSearchParams();
  const [code, setCode] = useState(searchParams.get("code") ?? "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [successEmail, setSuccessEmail] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (successEmail) {
    return (
      <AuthPageShell title="Account created">
        <div className="space-y-4">
          <InlineError tone="success" message={`You can now sign in as ${successEmail}.`} />
          <Link
            to="/login"
            className="inline-flex h-9 w-full items-center justify-center rounded-[var(--radius-md)] bg-[var(--color-accent)] text-sm font-medium text-white hover:bg-[var(--color-accent-hover)]"
          >
            Go to sign in
          </Link>
        </div>
      </AuthPageShell>
    );
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      setSubmitting(false);
      return;
    }

    const result = await signupWithInviteCode({ code, email, password });
    if (!result.ok) {
      setError(result.error ?? "Signup failed.");
      setSubmitting(false);
      return;
    }

    setSuccessEmail(result.email ?? email.trim().toLowerCase());
    setSubmitting(false);
  };

  return (
    <AuthPageShell title="Create account">
      <form className="space-y-4" onSubmit={(event) => void handleSubmit(event)}>
        <label className="block space-y-1.5">
          <span className="text-sm font-medium text-[var(--color-text)]">Invite code</span>
          <Input
            value={code}
            onChange={(event) => setCode(event.target.value.toUpperCase())}
            autoComplete="off"
            spellCheck={false}
            required
            placeholder="ABCD2345"
          />
        </label>

        <label className="block space-y-1.5">
          <span className="text-sm font-medium text-[var(--color-text)]">Email</span>
          <Input
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
        </label>

        <label className="block space-y-1.5">
          <span className="text-sm font-medium text-[var(--color-text)]">Password</span>
          <Input
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            minLength={8}
            required
          />
        </label>

        <label className="block space-y-1.5">
          <span className="text-sm font-medium text-[var(--color-text)]">Confirm password</span>
          <Input
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            minLength={8}
            required
          />
        </label>

        {error ? <InlineError message={error} /> : null}

        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? "Creating account…" : "Create account"}
        </Button>
      </form>

      <p className="mt-4 text-center text-sm text-[var(--color-text-muted)]">
        Already have an account?{" "}
        <Link to="/login" className="font-medium text-[var(--color-accent)] hover:underline">
          Sign in
        </Link>
      </p>
    </AuthPageShell>
  );
}
